import {
  ShiftManagementAddRestDaysDocument,
  ShiftManagementAreasViewPageDocument,
  ShiftManagementMainPageDocument,
} from 'generated/resources/graphql'
import { uniq } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import type { RestDayParams } from 'modules/shift_management/lib/buildAddRestDaysParameters'
import type { View } from 'modules/shift_management/lib/types'
import RestDay from 'modules/shift_management/store/internal/models/RestDay'

import { useShiftsRows } from './useShiftsRows'
import { useUI } from './useUI'

const trackAddEvent = (restDays: RestDay[], view: View) => {
  const employeeCount = uniq(restDays.map((restDay) => restDay.employeeId)).length

  return track(trackingEvents.shiftManagement.addRestDays, {
    number_of_impacted_employees: employeeCount,
    number_of_impacted_rest_days: restDays.length,
    view: view.viewType,
    view_type_by: view.groupType,
  })
}

export interface Props {
  params: RestDayParams[]
  onSuccess?: () => void
}

const useAddRestDays = ({ params, onSuccess }: Props) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchUI] = useUI()

  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()

  const { refreshStats } = useRefreshStats()

  const [bulkCreateRestDays, { loading }] = useMutation(ShiftManagementAddRestDaysDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkCreateShiftmanagementRestDay,
  })

  const onSubmit = async () => {
    const result = await bulkCreateRestDays({
      variables: { restDays: params.map((param) => ({ ...param, companyId: currentCompanyId })) },
      // TODO: Actually update the cache properly
      refetchQueries: [ShiftManagementMainPageDocument, ShiftManagementAreasViewPageDocument],
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      const restDays = (handled.data?.restDays || []).map((restDay) => {
        return RestDay.build({
          id: restDay.id,
          employee_id: restDay.employee?.id || 0,
          date: restDay.date,
          state: restDay.state,
          timezone: restDay.timezone || '',
          employeeFullName: restDay.employee?.fullName || '',
          employeeAvatar: restDay.employee?.avatar?.url || '',
        })
      })

      dispatchUI({ type: 'REFRESHING_STATS' })
      dispatchSchedule({ type: 'ADD_REST_DAYS', restDays })
      dispatchShiftsRows({ type: 'ADD_REST_DAYS', restDays })

      if (onSuccess) onSuccess()

      trackAddEvent(restDays, view)
      await refreshStats(shiftsRows)

      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.add_rest_day_success'),
      })
    }
  }

  return {
    submitting: loading,
    onSubmit,
  }
}

export { useAddRestDays }
